import { Maximize, Pause, Play, Settings, Volume2, VolumeX } from 'lucide-react'
import * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

import { Button } from '@/components/ui/button'

import uploadService from '@/services/upload.service'

const VIMEO_STATUS_POLL_MS = 12_000
const PROCESSING_MESSAGE = 'This video is still processing. Please check back in a few minutes.'

type VimeoPlaybackUiStatus = 'checking' | 'processing' | 'ready' | 'error'

interface CourseVideoPlayerProps {
  videoUrl: string
  className?: string
  autoPlay?: boolean
  controls?: boolean
  onVideoCompleted?: () => void
  videoId?: number
}

function extractVimeoVideoId(url: string): string | null {
  try {
    const urlObj = new URL(url)
    if (!urlObj.hostname.includes('vimeo.com')) {
      return null
    }
    const match = urlObj.pathname.match(/\/(?:video\/)?(\d+)/)
    return match?.[1] ?? null
  } catch {
    return null
  }
}

export function CourseVideoPlayer({ videoUrl, className, autoPlay = false, controls = true }: CourseVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [showControls, setShowControls] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [vimeoStatus, setVimeoStatus] = useState<VimeoPlaybackUiStatus>('checking')

  const isValidVideoUrl = useCallback((url: string) => {
    if (!url) return false
    try {
      const urlObj = new URL(url)
      const validProtocols = ['http:', 'https:', 'blob:']
      const validExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv']

      if (!validProtocols.includes(urlObj.protocol)) return false

      const pathname = urlObj.pathname.toLowerCase()
      const hasVideoExtension = validExtensions.some(ext => pathname.includes(ext))
      const isStreamingUrl =
        urlObj.hostname.includes('sproutvideo') ||
        urlObj.hostname.includes('videos.sproutvideo') ||
        urlObj.hostname.includes('stream') ||
        urlObj.hostname.includes('video')

      return hasVideoExtension || isStreamingUrl
    } catch {
      return false
    }
  }, [])

  const isEmbeddableUrl = (url: string) => {
    if (!url) return false
    try {
      const urlObj = new URL(url)
      return (
        urlObj.hostname.includes('sproutvideo') ||
        urlObj.hostname.includes('videos.sproutvideo') ||
        urlObj.hostname.includes('vimeo.com') ||
        urlObj.hostname.includes('player.vimeo.com')
      )
    } catch {
      return false
    }
  }

  const isVimeoUrl = (url: string) => {
    try {
      const urlObj = new URL(url)
      return urlObj.hostname.includes('vimeo.com')
    } catch {
      return false
    }
  }

  useEffect(() => {
    if (!isVimeoUrl(videoUrl)) {
      setVimeoStatus('ready')
      return
    }

    const vimeoId = extractVimeoVideoId(videoUrl)
    if (!vimeoId) {
      setVimeoStatus('error')
      return
    }

    let cancelled = false
    let pollTimer: ReturnType<typeof setTimeout> | undefined
    let consecutiveFailures = 0
    const maxConsecutiveFailures = 3

    const pollStatus = async () => {
      try {
        const result = await uploadService.getVideoStatus(vimeoId)
        if (cancelled) return

        consecutiveFailures = 0

        if (result.status === 'ready') {
          setVimeoStatus('ready')
          return
        }
        if (result.status === 'error') {
          setVimeoStatus('error')
          return
        }

        setVimeoStatus('processing')
        pollTimer = setTimeout(() => {
          void pollStatus()
        }, VIMEO_STATUS_POLL_MS)
      } catch {
        if (cancelled) return
        consecutiveFailures += 1
        if (consecutiveFailures >= maxConsecutiveFailures) {
          setVimeoStatus('error')
          return
        }
        // Transient failures: keep processing UX so users never see Vimeo's
        // generic "having a little trouble" screen while status is unknown.
        setVimeoStatus(prev => (prev === 'ready' ? prev : 'processing'))
        pollTimer = setTimeout(() => {
          void pollStatus()
        }, VIMEO_STATUS_POLL_MS)
      }
    }

    setVimeoStatus('checking')
    void pollStatus()

    return () => {
      cancelled = true
      if (pollTimer) clearTimeout(pollTimer)
    }
  }, [videoUrl])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    if (!isValidVideoUrl(videoUrl)) {
      setHasError(true)
      setIsLoading(false)
      return
    }

    const handleTimeUpdate = () => {
      const currentVideoTime = video.currentTime
      setCurrentTime(currentVideoTime)
    }
    const handleLoadedMetadata = () => {
      setDuration(video.duration)
      setIsLoading(false)
    }
    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)
    const handleError = (_e: Event) => {
      setHasError(true)
      setIsLoading(false)
    }
    const handleLoadStart = () => {
      setIsLoading(true)
      setHasError(false)
    }
    const handleCanPlay = () => {
      setIsLoading(false)
    }

    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('loadedmetadata', handleLoadedMetadata)
    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('error', handleError)
    video.addEventListener('loadstart', handleLoadStart)
    video.addEventListener('canplay', handleCanPlay)

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('loadedmetadata', handleLoadedMetadata)
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('error', handleError)
      video.removeEventListener('loadstart', handleLoadStart)
      video.removeEventListener('canplay', handleCanPlay)
    }
  }, [videoUrl, isValidVideoUrl])

  const togglePlay = () => {
    const video = videoRef.current
    if (!video) return

    if (isPlaying) {
      video.pause()
    } else {
      video.play()
    }
  }

  const toggleMute = () => {
    const video = videoRef.current
    if (!video) return

    video.muted = !isMuted
    setIsMuted(!isMuted)
  }

  const toggleFullscreen = () => {
    const video = videoRef.current
    if (!video) return

    if (!document.fullscreenElement) {
      video.requestFullscreen()
    } else {
      document.exitFullscreen()
    }
  }

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current
    if (!video) return

    const time = parseFloat(e.target.value)
    video.currentTime = time
    setCurrentTime(time)
  }

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const _progress = duration > 0 ? (currentTime / duration) * 100 : 0
  const showVimeoStatusOverlay = isVimeoUrl(videoUrl) && vimeoStatus !== 'ready'
  const canEmbed = isEmbeddableUrl(videoUrl) && (!isVimeoUrl(videoUrl) || vimeoStatus === 'ready')

  return (
    <div
      className={cn('relative bg-black rounded-lg overflow-hidden group', className)}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      {showVimeoStatusOverlay ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black px-6">
          <div className="text-center text-white max-w-md">
            {vimeoStatus === 'checking' ? (
              <>
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white mx-auto mb-4" />
                <p className="text-lg">Loading video...</p>
              </>
            ) : vimeoStatus === 'processing' ? (
              <p className="text-lg leading-relaxed">{PROCESSING_MESSAGE}</p>
            ) : (
              <>
                <h3 className="text-xl font-semibold mb-2">Video unavailable</h3>
                <p className="text-sm text-gray-300">
                  This video could not be processed. Please try again later or contact support.
                </p>
              </>
            )}
          </div>
        </div>
      ) : null}

      {canEmbed ? (
        <iframe
          src={videoUrl}
          className="w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          title="Video Player"
        />
      ) : !isEmbeddableUrl(videoUrl) ? (
        <video
          ref={videoRef}
          src={videoUrl}
          className="w-full h-full object-cover"
          autoPlay={autoPlay}
          muted={isMuted}
          onClick={togglePlay}
          preload="metadata"
          crossOrigin="anonymous"
          playsInline
        />
      ) : (
        <div className="w-full h-full min-h-[12rem]" aria-hidden />
      )}

      {/* Only show overlays and controls for direct video files, not embedded videos (SproutVideo/Vimeo) */}
      {!isEmbeddableUrl(videoUrl) && (
        <>
          <div className="absolute inset-0 bg-black/20" />

          {isLoading && !hasError && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <div className="text-center text-white">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                <p className="text-lg">Loading video...</p>
              </div>
            </div>
          )}

          {hasError && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80">
              <div className="text-center text-white p-6">
                <div className="text-6xl mb-4">⚠️</div>
                <h3 className="text-xl font-semibold mb-2">Video Error</h3>
                <p className="text-sm mb-4">
                  {!isValidVideoUrl(videoUrl)
                    ? 'Invalid video URL format. Please check the video source.'
                    : 'Unable to load the video. Please check your internet connection or try again.'}
                </p>
                <p className="text-xs text-gray-300 break-all max-w-md mx-auto">{videoUrl}</p>
                <Button
                  onClick={() => {
                    setHasError(false)
                    setIsLoading(true)
                    if (videoRef.current) {
                      videoRef.current.load()
                    }
                  }}
                  className="mt-4"
                  variant="outline"
                >
                  Retry
                </Button>
              </div>
            </div>
          )}

          {!isPlaying && !isLoading && !hasError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Button
                size="lg"
                className="w-16 h-16 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm"
                onClick={togglePlay}
              >
                <Play className="w-8 h-8 text-white ml-1" />
              </Button>
            </div>
          )}

          {showControls && controls && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
              <div className="mb-4">
                <input
                  type="range"
                  min="0"
                  max={duration || 0}
                  value={currentTime}
                  onChange={handleSeek}
                  className="w-full h-1 bg-white/30 rounded-lg appearance-none cursor-pointer slider"
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Button size="sm" variant="ghost" className="text-white hover:bg-white/20" onClick={togglePlay}>
                    {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  </Button>

                  <Button size="sm" variant="ghost" className="text-white hover:bg-white/20" onClick={toggleMute}>
                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </Button>

                  <span className="text-white text-sm">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <Button size="sm" variant="ghost" className="text-white hover:bg-white/20">
                    <Settings className="w-5 h-5" />
                  </Button>

                  <Button size="sm" variant="ghost" className="text-white hover:bg-white/20" onClick={toggleFullscreen}>
                    <Maximize className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <style jsx>{`
        .slider {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          cursor: pointer;
        }

        .slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          height: 12px;
          width: 12px;
          border-radius: 50%;
          background: #ffffff;
          cursor: pointer;
          box-shadow: 0 0 2px 0 #555;
        }

        .slider::-moz-range-thumb {
          height: 12px;
          width: 12px;
          border-radius: 50%;
          background: #ffffff;
          cursor: pointer;
          border: none;
          box-shadow: 0 0 2px 0 #555;
        }
      `}</style>
    </div>
  )
}
